import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Shield, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const FALLBACK_TERMS_VERSION = "1.0";
const API = process.env.REACT_APP_BACKEND_URL || '';

const FALLBACK_TERMS_CONTENT = `## Welcome to Thrive

By using this application, you agree to the following terms and conditions. Please read them carefully before proceeding.

## Data Storage & Privacy
- Your data is stored on secure cloud servers managed by Emergent Labs
- We collect and store information you provide including: personal details, apiary locations, hive data, inspection records, and uploaded images
- Your GPS coordinates are used for community features (Find Beekeepers map) only if you opt-in
- We do not sell or share your personal data with third parties for marketing purposes
- You may request deletion of your data by contacting the administrator

## Service Availability & Downtime
- This service is provided "as is" without warranty of any kind
- We strive for high availability but **do not guarantee 100% uptime**
- Scheduled maintenance may occur with or without prior notice
- We recommend maintaining offline backups of critical data

## Limitation of Liability
- **NO LIABILITY FOR DATA LOSS:** We are not responsible for any loss, corruption, or unavailability of your data due to technical failures, server issues, hacking, natural disasters, or any other cause
- **NO LIABILITY FOR DAMAGES:** We are not liable for any direct, indirect, incidental, special, or consequential damages resulting from your use or inability to use this service
- **NO LIABILITY FOR DECISIONS:** Any beekeeping decisions made based on data in this application are your sole responsibility
- **BACKUP RESPONSIBILITY:** You are responsible for maintaining your own backups of important data

## User Responsibilities
- Keep your login credentials secure and confidential
- Provide accurate information when creating your account
- Use the service in compliance with all applicable laws
- Do not upload inappropriate, illegal, or harmful content
- Respect other users in community features (Hive Mind, Marketplace)

## Security Measures
- Passwords are encrypted using industry-standard hashing
- All data transmission is encrypted via HTTPS
- Access tokens expire after 30 days for security

## Changes to Terms
We reserve the right to modify these terms at any time. Continued use of the service after changes constitutes acceptance of the new terms.

## Contact
For questions about these terms or to request data deletion, contact: macmanclan@gmail.com`;

const TermsAcceptanceDialog = ({ onAccept, loading, isUpdate, onSkip }) => {
  const [agreed, setAgreed] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsVersion, setTermsVersion] = useState(FALLBACK_TERMS_VERSION);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const res = await fetch(`${API}/api/legal-documents/terms`);
        if (res.ok) {
          const data = await res.json();
          if (data.content && data.is_published !== false) {
            setTermsContent(data.content);
            setTermsVersion(data.version || FALLBACK_TERMS_VERSION);
          } else {
            setTermsContent(FALLBACK_TERMS_CONTENT);
          }
        } else {
          setTermsContent(FALLBACK_TERMS_CONTENT);
        }
      } catch {
        setTermsContent(FALLBACK_TERMS_CONTENT);
      } finally {
        setFetching(false);
      }
    };
    fetchTerms();
  }, []);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">{isUpdate ? 'Updated Terms & Conditions' : 'Terms & Conditions'}</h1>
              <p className="text-amber-100 text-sm">
                {isUpdate ? 'Our terms have been updated — please review and re-accept' : 'Please read and accept before continuing'}
                {' '}(v{termsVersion})
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <ScrollArea
          className="h-[50vh] p-6"
          onScrollCapture={handleScroll}
        >
          {fetching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-amber-600 mr-2" />
              <span className="text-stone-500">Loading terms...</span>
            </div>
          ) : (
            <div className="prose prose-stone prose-sm max-w-none">
              <ReactMarkdown>{termsContent}</ReactMarkdown>
              <div className="text-center text-xs text-stone-400 mt-6">
                Terms Version: {termsVersion}
              </div>
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="border-t border-stone-200 p-4 bg-stone-50">
          {!scrolledToBottom && (
            <p className="text-xs text-amber-600 mb-3 text-center">
              Please scroll down to read all terms before accepting
            </p>
          )}

          <div className="flex items-start gap-3 mb-4">
            <Checkbox
              id="terms-checkbox"
              checked={agreed}
              onCheckedChange={setAgreed}
              disabled={!scrolledToBottom || fetching}
              className="mt-0.5"
            />
            <label
              htmlFor="terms-checkbox"
              className={`text-sm cursor-pointer ${!scrolledToBottom ? 'text-stone-400' : 'text-stone-700'}`}
            >
              I have read, understood, and agree to the Terms & Conditions, Privacy Policy,
              and acknowledge that the service provider is <strong>not liable for any data loss
              or damages</strong>.
            </label>
          </div>

          <Button
            onClick={() => onAccept(termsVersion)}
            disabled={!agreed || loading || fetching}
            className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-stone-300"
          >
            {loading ? 'Processing...' : `I Accept v${termsVersion} — Continue to Application`}
          </Button>
          {onSkip && (
            <button
              onClick={onSkip}
              className="w-full text-center text-xs text-stone-400 hover:text-stone-600 mt-2 py-1"
              data-testid="skip-terms-btn"
            >
              Skip for now (you'll be asked again next login)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { FALLBACK_TERMS_VERSION as TERMS_VERSION };
export default TermsAcceptanceDialog;
